<template>
  <div
    class="absolute right-1 top-2 bottom-2 w-1.5 rounded-full bg-brown-100 pointer-events-none transition-opacity duration-200"
    :class="scrollable ? 'opacity-100' : 'opacity-0'"
    aria-hidden="true"
  >
    <div class="w-full rounded-full bg-brown-800" :style="thumbStyle" />
  </div>
</template>

<script>
// Read-only scroll indicator for a layout's <ion-content>. The thumb's height
// mirrors the viewport-to-content ratio, so its size tells the reader how much
// is left and its position tells them how far down they are. Faded out — never
// display:none, the track has to stay measurable — when nothing overflows.
const MIN_THUMB_PX = 24;

export default {
  name: 'ScrollProgressBar',
  props: {
    // The <ion-content> that scrolls. Passed in rather than found by walking up
    // the DOM: the layout rendering this already holds the ref.
    contentEl: {
      type: Object,
      required: true,
    },
    // The element that actually grows with the content. Every wrapper between
    // ion-content and the page body is a flex item pinned to the viewport
    // height, so this is the only box whose resize reports new content.
    measureEl: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      scrollable: false,
      thumbTop: 0,
      thumbHeight: MIN_THUMB_PX,
    };
  },
  computed: {
    thumbStyle() {
      return {
        height: `${this.thumbHeight}px`,
        transform: `translateY(${this.thumbTop}px)`,
      };
    },
  },
  async mounted() {
    if (typeof this.contentEl.componentOnReady === 'function') {
      await this.contentEl.componentOnReady();
    }
    const scrollEl = await this.contentEl.getScrollElement();
    // The awaits above can outlive the component — open a lesson and hit back
    // immediately and beforeUnmount has already run, with nothing registered
    // yet to tear down.
    if (this.destroyed) return;
    this.scrollEl = scrollEl;
    this.scrollEl.addEventListener('scroll', this.schedule, { passive: true });
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(this.schedule);
      // measureEl reports content growth (a page turn, a late reflow, an image
      // landing); scrollEl reports viewport changes (rotation, keyboard).
      [this.measureEl, this.scrollEl]
        .filter(Boolean)
        .forEach(el => this.resizeObserver.observe(el));
    }
    this.update();
  },
  beforeUnmount() {
    this.destroyed = true;
    cancelAnimationFrame(this.rafId);
    if (this.resizeObserver) this.resizeObserver.disconnect();
    if (this.scrollEl) this.scrollEl.removeEventListener('scroll', this.schedule);
  },
  methods: {
    // Coalesce scroll and resize bursts into one measurement per frame.
    schedule() {
      cancelAnimationFrame(this.rafId);
      this.rafId = requestAnimationFrame(() => this.update());
    },
    update() {
      if (this.destroyed || !this.scrollEl || !this.$el) return;
      const { scrollTop, scrollHeight, clientHeight } = this.scrollEl;
      const maxScroll = scrollHeight - clientHeight;
      this.scrollable = maxScroll > 1;
      if (!this.scrollable) return;
      const trackHeight = this.$el.clientHeight;
      const height = Math.max(trackHeight * (clientHeight / scrollHeight), MIN_THUMB_PX);
      const progress = Math.min(Math.max(scrollTop / maxScroll, 0), 1);
      this.thumbHeight = height;
      // The min-height clamp can make the thumb taller than a very short
      // track — never let it travel to a negative offset.
      this.thumbTop = progress * Math.max(trackHeight - height, 0);
    },
  },
};
</script>
